import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { ADMIN_ROUTES } from 'src/constants/routes'
import { PAYMENTS_OPS_ITEMS } from 'src/pages/payments-ops/paymentsOpsNav'
import type { AdminApp, AdminAppGroup } from './adminApps.types'

// Derived, not hand-listed: a new Payments Ops rail item can't be left off the tile.
const PAYMENTS_OPS_PERMISSIONS: InternalPermission[] = Array.from(
  new Set(
    PAYMENTS_OPS_ITEMS.flatMap((item) => {
      return item.requiredAnyPermission
    }),
  ),
)

export const ADMIN_APP_GROUPS: AdminAppGroup[] = [
  {
    label: 'Partner Operations',
    apps: [
      {
        id: 'admin-dashboard',
        path: ADMIN_ROUTES.ADMIN_DASHBOARD_URL,
        title: 'Admin Dashboard',
        description:
          'Partners, onboarding, payments, users, insurance, invoices, OAuth and webhooks — the core admin surface.',
        iconName: 'gs_buildings',
        isAvailable: true,
        issueId: 'ENG-473',
        requiredAnyPermission: [
          InternalPermission.PartnersView,
          InternalPermission.InsuranceProductsView,
          InternalPermission.LmsTrainingsView,
        ],
      },
      {
        id: 'update-partner-driver',
        path: ADMIN_ROUTES.UPDATE_PARTNER_DRIVER_URL,
        title: 'Update Partner Driver',
        // No "partner assignment": nothing in the internal API moves a driver to another partner.
        description: 'Look up a driver, update their profile, and manage their onboarding steps.',
        iconName: 'gs_driver_front',
        isAvailable: true,
        issueId: 'ENG-486',
        requiredAnyPermission: [InternalPermission.ContractorsView],
      },
    ],
  },
  {
    label: 'Document Review',
    apps: [
      {
        id: 'internal-document-review',
        path: ADMIN_ROUTES.INTERNAL_DOCUMENT_REVIEW_URL,
        title: 'Internal Document Review',
        description: 'Review, approve or reject submitted driver documents in a guided queue.',
        iconName: 'gs_notepad',
        isAvailable: true,
        issueId: 'ENG-489',
        requiredAnyPermission: [
          InternalPermission.DocumentReviewView,
          InternalPermission.McDocumentReviewView,
        ],
      },
    ],
  },
  {
    label: 'Payments',
    apps: [
      {
        id: 'payments-ops',
        path: ADMIN_ROUTES.PAYMENTS_OPS_URL,
        title: 'Payments Ops',
        description:
          'Review and approve payments, reconcile transactions, manage refunds, bank onboarding and US Bank killswitches.',
        iconName: 'gs_wallet_3',
        isAvailable: true,
        issueId: 'ENG-491',
        requiredAnyPermission: PAYMENTS_OPS_PERMISSIONS,
      },
    ],
  },
  {
    label: 'Automation',
    apps: [
      {
        id: 'schedulers-management',
        path: ADMIN_ROUTES.SCHEDULERS_MANAGEMENT_URL,
        title: 'Schedulers Management',
        description: 'Inspect and manage scheduled jobs and their run history.',
        iconName: 'gs_tasks',
        isAvailable: true,
        issueId: 'ENG-494',
        requiredAnyPermission: [InternalPermission.SchedulersView],
      },
    ],
  },
  {
    label: 'Platform',
    apps: [
      {
        id: 'remote-config',
        path: ADMIN_ROUTES.REMOTE_CONFIG_URL,
        title: 'Remote Config',
        description:
          'Values contractor onboarding and the customer dashboard read at load, changed without a deploy.',
        iconName: 'gs_settings_gear_1',
        isAvailable: true,
        issueId: 'ENG-1309',
        requiredAnyPermission: [InternalPermission.RemoteConfigView],
      },
    ],
  },
  {
    label: 'Access',
    apps: [
      {
        id: 'users-management',
        path: ADMIN_ROUTES.USERS_MANAGEMENT_URL,
        title: 'Users Management',
        description:
          'Internal admin users and roles — assign roles, disable access and edit role permissions.',
        iconName: 'gs_user_group',
        isAvailable: true,
        issueId: 'ENG-1207',
        requiredAnyPermission: [
          InternalPermission.InternalUsersView,
          InternalPermission.InternalRolesView,
        ],
      },
    ],
  },
]

export const ADMIN_APPS: AdminApp[] = ADMIN_APP_GROUPS.flatMap((group) => {
  return group.apps
})

export const findAdminAppByPathname = (pathname: string): AdminApp | undefined => {
  return ADMIN_APPS.find((app) => {
    return pathname === app.path || pathname.startsWith(`${app.path}/`)
  })
}

/** The route-level requirement for an app (`RequirePermission` in AppRoutes). */
export const getAdminAppPermissions = (appId: string): InternalPermission[] => {
  const app = ADMIN_APPS.find((candidate) => {
    return candidate.id === appId
  })

  // An unknown id must fail loudly, not return []: `RequirePermission` reads [] as "open to
  // everyone", so a renamed app would silently lose its route gate. Called at module load by
  // AppRoutes, so a stale id breaks the app (and its tests) immediately.
  if (!app) {
    throw new Error(`Unknown admin app id: ${appId}`)
  }

  return app.requiredAnyPermission ?? []
}
